import type { ReactNode } from 'react'
import { X } from 'lucide-react'
import s from './GridCommandBar.module.css'

/**
 * Whether a collection's governed COMMAND BUTTONS sit beside the mark (#2375).
 *
 * Canonical values are `shown` and `hidden` (ADR F-84 §3). `auto` was the old
 * governed default meaning "a floating bar appears on selection"; the floating
 * bar is deleted, selection actions are always available from the first
 * selected row, and a stored `auto` now reads as `shown` for ONE compatibility
 * release (`normalizeCommandBar`) before it is removed through the governed
 * migration path.
 */
export type GridCommandBarPreference = 'shown' | 'hidden'

/** The legacy value a stored document or setting may still carry this release. */
export type LegacyGridCommandBarPreference = GridCommandBarPreference | 'auto'

/**
 * The GOVERNED document's value, read with the one-release `auto` alias.
 * Unknown ⇒ `shown`: the command buttons are the governed, already-authorised
 * affordances, and hiding them on a typo would be the quiet failure.
 */
export const normalizeCommandBar = (value: unknown): GridCommandBarPreference =>
  value === 'hidden' ? 'hidden' : 'shown'

/** Selection actions show from ONE selected row, whatever the preference says. */
export const gridSelectionVisible = (selectionCount: number): boolean => selectionCount >= 1

/** Command buttons show only when nothing is selected and the preference is `shown`. */
export const gridCommandsVisible = (
  preference: GridCommandBarPreference,
  selectionCount: number,
): boolean => selectionCount === 0 && preference === 'shown'

export interface GridSelectionProps {
  /** The current controlled selection size (≥ 1 when this renders). */
  count: number
  /** A specific accessible name: "Actions for selected opportunities". */
  label: string
  /** Clears the selection. */
  onClear: () => void
  /** Already-authorised batch actions (governed `cardinality = 'batch'`). */
  children?: ReactNode
  testId?: string
}

/**
 * THE INLINE SELECTION GROUP (#2375) — `[ N selected ✕ ]  [Act] [Act]`.
 *
 * It replaces the governed commands IN PLACE, in the collection bar's leading
 * slot, from the first selected row. The fixed floating pill it replaces
 * (`position: fixed`, a reveal keyframe and a safe-area offset) is deleted: it
 * covered the rows it acted on and appeared only at two selected. Selection is
 * per grid instance — a nested grid's group lives in the nested bar.
 *
 * The bar's height does not change between the two states: the group's
 * controls are the bar's own small controls, on the same row.
 */
export function GridSelection({
  count, label, onClear, children, testId = 'grid-selection',
}: GridSelectionProps) {
  if (!gridSelectionVisible(count)) return null
  return (
    <div className={s.group} role="group" aria-label={label} data-testid={testId}>
      <span className={s.count}>
        <span aria-live="polite" data-testid="grid-selection-count" className="tnum">{count} selected</span>
        <button type="button" className={s.clear} aria-label="Clear selection"
          data-testid="grid-selection-clear" onClick={onClear}>
          <X size={12} strokeWidth={2.5} aria-hidden="true" />
        </button>
      </span>
      {children != null && <span className={s.actions}>{children}</span>}
    </div>
  )
}
